'use client';

import * as React from 'react';
import {useState, useEffect} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
  ContentCard,
  ContentPlaceholderCard
} from '@purpose/ui/components/ContentCard';
import {cn} from '@purpose/ui/lib/utils';
import Image from 'next/image';
import CarouselArrow from '../../assets/static/CarouselArrow.svg';
import {useResponsive} from '@purpose/ui/hooks';

interface CarouselCard {
  logo: string;
  name?: string;
  description?: string;
  subtitle?: string;
  id: number;
  organisation_id?: number;
  isRequestSent?: boolean;
  is_request_sent?: boolean;
  organisaton_id?: number;
  is_subscribed?: boolean;
  category_name?: string;
  category_key?: string;
  organisation_name?: string;
}

interface CarouselProps {
  cards: CarouselCard[];
  onCardClick?: (id: number, orgId?: number) => void;
  getDescription?: (card: CarouselCard) => string | undefined;
  renderCard?: (card: CarouselCard) => React.ReactNode;
  buttonIconColor?: string | null;
  cardWrapperClassname?: string;
  placeholderProps?: {
    Icon?: any;
    withTitle?: boolean;
    withSubtitle?: boolean;
    description?: string;
    buttonProps?: {
      title: string;
      Icon?: any;
      onClick: () => void;
    };
  };
  customSlidesPerView?: {[key: string]: number};
  overtitle?: (b: any) => React.JSX.Element;
  renderLastCard?: () => React.ReactNode;
}

const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  (
    {
      cards,
      onCardClick,
      getDescription,
      renderCard,
      buttonIconColor,
      cardWrapperClassname,
      placeholderProps,
      customSlidesPerView,
      overtitle,
      renderLastCard
    },
    ref
  ) => {
    const {breakpoint} = useResponsive();
    const [emblaRef, emblaApi] = useEmblaCarousel({
      loop: false,
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'trimSnaps'
    });
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [canScrollPrev, setCanScrollPrev] = useState(false);

    const updateScrollButtons = () => {
      if (!emblaApi) return;
      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
    };

    useEffect(() => {
      if (!emblaApi) return;

      updateScrollButtons();
      emblaApi.on('select', updateScrollButtons);
      emblaApi.on('reInit', updateScrollButtons);

      return () => {
        emblaApi.off('select', updateScrollButtons);
        emblaApi.off('reInit', updateScrollButtons);
      };
    }, [emblaApi]);

    const slidesPerView = customSlidesPerView || {
      xs: 2,
      sm: 3,
      md: 4,
      lg: 5,
      xl: 5
    };
    const placeholderCardNumber = Math.max(
      0,
      // @ts-ignore
      slidesPerView?.[breakpoint] - cards.length
    );
    const showPlaceholderCards = !!placeholderProps && placeholderCardNumber;

    return (
      <div
        className="embla relative"
        ref={emblaRef}
        style={{
          ['--slide-spacing' as any]: '16px',
          ['--slides-per-view' as any]: slidesPerView[breakpoint]
        }}
      >
        <div className="embla__container flex gap-4">
          {cards.map(({logo, name, ...card}, index) => (
            <div
              className={cn(
                'embla__slide shrink-0',
                // ((slidesPerView - 1) * gapWidth) -- totalGapWidth
                // (100% - totalGapWidth) -- totalCardWidth
                // (totalCardWidth / slidesPerView) -- cardWidth
                'w-[calc((100%-((var(--slides-per-view)-1)*var(--slide-spacing)))/var(--slides-per-view))]',
                'pb-3',
                cardWrapperClassname
              )}
              key={`${card.id}_${card?.organisation_id}_${index}`}
            >
              {renderCard ? (
                renderCard({logo, name, ...card})
              ) : (
                <ContentCard
                  image={logo}
                  title={name}
                  {...card}
                  {...(overtitle && {
                    overtitle: overtitle(card)
                  })}
                  {...(getDescription && {
                    description: getDescription({logo, name, ...card})
                  })}
                  {...(onCardClick && {
                    onClick: () => onCardClick(card.id, card?.organisation_id)
                  })}
                />
              )}
            </div>
          ))}
          {renderLastCard ? (
            <div
              className={cn(
                'embla__slide flex-shrink-0',
                // (slidesPerView - 1) * gapWidth) -- totalGapWidth
                // (100% - totalGapWidth) -- totalCardWidth
                // (totalCardWidth / slidesPerView) -- cardWidth
                'w-[calc((100%-((var(--slides-per-view)-1)*var(--slide-spacing)))/var(--slides-per-view))]',
                'flex pb-3',
                cardWrapperClassname
              )}
              key={`last-card`}
            >
              {renderLastCard()}
            </div>
          ) : null}

          {!!showPlaceholderCards &&
            Array.from({length: placeholderCardNumber}).map((item, index) => (
              <div
                className={cn(
                  'embla__slide flex-shrink-0',
                  'w-[calc((100%-((var(--slides-per-view)-1)*var(--slide-spacing)))/var(--slides-per-view))]',
                  'pb-3',
                  cardWrapperClassname
                )}
                key={`${cards?.[0]?.id}-placeholder-${index}`}
              >
                <ContentPlaceholderCard {...placeholderProps} />
              </div>
            ))}
        </div>
        {canScrollNext && (
          <>
            <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-16 bg-gradient-to-l from-white to-transparent" />
            <button
              className="absolute top-1/2 right-2 z-20 -translate-y-1/2 cursor-pointer rounded-full"
              onClick={() => emblaApi && emblaApi.scrollNext()}
              style={{color: buttonIconColor || undefined}}
              aria-label="Neste"
            >
              <Image alt="next" src={CarouselArrow} width={68} height={68} />
            </button>
          </>
        )}
        {canScrollPrev && (
          <>
            <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-16 bg-gradient-to-r from-white to-transparent" />
            <button
              className="absolute top-1/2 left-2 z-20 -translate-y-1/2 cursor-pointer rounded-full"
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              style={{color: buttonIconColor || undefined}}
              aria-label="Forrige"
            >
              <Image
                alt="next"
                src={CarouselArrow}
                width={68}
                height={68}
                className="rotate-180"
              />
            </button>
          </>
        )}
      </div>
    );
  }
);

Carousel.displayName = 'Carousel';

export {Carousel};
export type {CarouselProps, CarouselCard};
